import { createAsyncThunk, isAsyncThunkAction } from '@reduxjs/toolkit';
import type { AsyncThunk, AsyncThunkAction, AsyncThunkPayloadCreator, Middleware } from '@reduxjs/toolkit';
import { sessionChanged } from './session';

const readTypes = new Set<string>();
const invalidators = new WeakMap<Function, Set<() => void>>();

// Writes can make an in-flight read stale. Invalidate at both start and settlement
// so reads during a save cannot absorb the refresh that follows that save.
export const invalidateReadRequests: Middleware = ({ getState }) => next => action => {
    if (sessionChanged.match(action) || (isAsyncThunkAction(action) && !readTypes.has(action.type.slice(0, action.type.lastIndexOf('/'))))) {
        invalidators.get(getState)?.forEach(clear => clear());
    }
    return next(action);
};

// Encode plain request arguments by value, including sorted object keys. Unsupported
// arguments bypass the guard rather than accidentally sharing an unrelated request.
function requestKey(value: unknown, ancestors = new Set<object>()): string {
    if (value === undefined) return 'undefined';
    if (value === null) return 'null';
    if (typeof value === 'string' || typeof value === 'boolean') return JSON.stringify(value);
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value !== 'object' || ancestors.has(value)) throw new Error('Unsupported request argument');
    ancestors.add(value);
    try {
        if (Array.isArray(value)) return '[' + value.map(item => requestKey(item, ancestors)).join(',') + ']';
        if (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null) {
            throw new Error('Expected a plain request argument');
        }
        return '{' + Object.keys(value).sort().map(key =>
            JSON.stringify(key) + ':' + requestKey((value as Record<string, unknown>)[key], ancestors)
        ).join(',') + '}';
    } finally {
        ancestors.delete(value);
    }
}

/**
 * Guard concurrent reads by thunk, store and arguments before Redux emits pending.
 * Duplicate callers share the original result (including unwrap), so awaiting screens
 * do not receive a synthetic condition rejection. Nothing is cached after settlement.
 * abort() cancels the shared request; callers needing independent cancellation can
 * supply an external signal, which bypasses sharing. Use only for read operations.
 */
export function createReadAsyncThunk<Returned, ThunkArg = void>(
    typePrefix: string,
    payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg>
): AsyncThunk<Returned, ThunkArg, {}> {
    const thunk = createAsyncThunk<Returned, ThunkArg>(typePrefix, payloadCreator);
    readTypes.add(typePrefix);
    type Action = AsyncThunkAction<Returned, ThunkArg, {}>;
    type Result = ReturnType<Action>;
    const stores = new WeakMap<Function, Map<string, Result>>();

    const guarded = (arg: ThunkArg, options?: { signal?: AbortSignal }): Action =>
        (dispatch, getState, extra) => {
            // The cast bridges RTK's conditional optional-argument signature.
            const execute = () => (thunk as (arg: ThunkArg, options?: { signal?: AbortSignal }) => Action)(
                arg, options
            )(dispatch, getState, extra);
            if (options?.signal) return execute();
            let key: string;
            try {
                key = requestKey(arg);
            } catch {
                return execute();
            }
            let pending = stores.get(getState);
            if (!pending) {
                pending = new Map();
                stores.set(getState, pending);
                let clears = invalidators.get(getState);
                if (!clears) {
                    clears = new Set();
                    invalidators.set(getState, clears);
                }
                const requests = pending;
                clears.add(() => requests.clear());
            }
            const existing = pending.get(key);
            if (existing) return existing;

            const result = execute();
            pending.set(key, result);
            const clear = () => {
                if (pending?.get(key) === result) pending.delete(key);
            };
            const abort = result.abort;
            result.abort = reason => {
                clear();
                abort(reason);
            };
            void result.then(clear, clear);
            return result;
        };

    // Preserve pending/fulfilled/rejected, settled and typePrefix for existing reducers.
    return Object.assign(guarded, thunk) as AsyncThunk<Returned, ThunkArg, {}>;
}
